import React from 'react';
import { Card, CardContent } from './ui/card';
import {
  motion,
  useInView,
  StaggerContainer,
  StaggerItem,
  FadeInWhenVisible
} from './animations';
import { useRef } from 'react';

const services = [
  {
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    title: 'Tratamientos Faciales',
    description: 'Limpieza profunda, hidratacion, antimanchas, dermapen y mas para una piel radiante.',
    treatments: ['Limpieza facial', 'Hidratacion', 'Antimanchas', 'Dermapen'],
    image: 'https://clinicaeranza.com/wp-content/uploads/2025/12/20251203_150940-scaled.jpg'
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    ),
    title: 'Tratamientos Corporales',
    description: 'Reductores, reafirmantes y anticeluliticos para esculpir tu figura ideal.',
    treatments: ['Reductores', 'Reafirmantes', 'Anticeluliticos', 'Drenaje linfatico'],
    image: 'https://clinicaeranza.com/wp-content/uploads/2024/03/20230715-143941.jpg'
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>
    ),
    title: 'Medicina Estetica',
    description: 'Botox, acido hialuronico, rellenos y otros tratamientos avanzados.',
    treatments: ['Botox', 'Acido hialuronico', 'Rellenos faciales', 'Bioestimulacion'],
    image: 'https://clinicaeranza.com/wp-content/uploads/2024/03/20230715-142917.jpg'
  },
  {
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    title: 'Postoperatorio',
    description: 'Recuperacion especializada post-cirugia estetica para mejores resultados.',
    treatments: ['Drenaje postqx', 'Radiofrecuencia', 'Presoterapia', 'Ultrasonidos'],
    image: 'https://images.unsplash.com/photo-1731514721772-329626f84c8b?w=600&q=80'
  }
];

const ServiceCard = ({ service, index }) => {
  const cardRef = useRef(null);
  const isInView = useInView(cardRef, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{
        duration: 0.6,
        delay: index * 0.15,
        ease: [0.25, 0.1, 0.25, 1]
      }}
    >
      <motion.div
        whileHover={{ y: -10 }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
      >
        <Card className="group hover:shadow-elegant transition-all duration-500 border-border/50 bg-gradient-card overflow-hidden rounded-2xl h-full">
          {/* Image with reveal animation */}
          <motion.div
            className="relative h-48 overflow-hidden"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.4 }}
          >
            <motion.img
              src={service.image}
              alt={service.title}
              loading="lazy"
              className="w-full h-full object-cover"
              initial={{ scale: 1.2 }}
              animate={isInView ? { scale: 1 } : {}}
              transition={{ duration: 0.8, ease: "easeOut" }}
              whileHover={{ scale: 1.1 }}
            />
            <motion.div
              className="absolute inset-0 bg-gradient-to-t from-card/90 to-transparent"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.2 }}
            />

            {/* Icon Overlay with bounce animation */}
            <motion.div
              className="absolute bottom-4 left-4 w-14 h-14 rounded-full bg-primary-muted flex items-center justify-center text-primary shadow-soft"
              initial={{ scale: 0, rotate: -180 }}
              animate={isInView ? { scale: 1, rotate: 0 } : {}}
              transition={{
                type: "spring",
                stiffness: 260,
                damping: 20,
                delay: 0.3 + index * 0.1
              }}
              whileHover={{
                scale: 1.15,
                rotate: 10,
                boxShadow: "0 10px 30px -10px rgba(244, 143, 177, 0.5)"
              }}
            >
              {service.icon}
            </motion.div>
          </motion.div>

          <CardContent className="p-6 flex flex-col h-full">
            {/* Title with hover effect */}
            <motion.h3
              className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors duration-300 font-serif"
              whileHover={{ x: 5 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              {service.title}
            </motion.h3>

            {/* Description */}
            <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
              {service.description}
            </p>

            {/* Treatments List with stagger animation */}
            <div className="mt-auto space-y-2">
              {service.treatments.map((treatment, idx) => (
                <motion.div
                  key={idx}
                  className="flex items-center gap-2 text-sm text-foreground/80"
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{
                    duration: 0.4,
                    delay: 0.5 + idx * 0.1 + index * 0.1
                  }}
                  whileHover={{ x: 5, color: "hsl(var(--primary))" }}
                >
                  <motion.div
                    className="w-1.5 h-1.5 rounded-full bg-primary"
                    whileHover={{ scale: 1.5 }}
                  />
                  <span>{treatment}</span>
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
};

const Services = () => {
  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto">
        {/* Section Header with animations */}
        <FadeInWhenVisible direction="up" className="text-center mb-16">
          <motion.h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            Nuestros{' '}
            <motion.span
              className="text-gradient-primary inline-block"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              Tratamientos
            </motion.span>
          </motion.h2>
          <motion.p
            className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Soluciones personalizadas para cada necesidad estetica
          </motion.p>
        </FadeInWhenVisible>

        {/* Services Grid with stagger */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
